/**
 * `builder` namespace — user-facing strings for the top-level Builder area
 * (`src/builder/`). FLAT camelCase keys, one per line (ADR 0065). Plural keys
 * use i18next `_one`/`_other` suffixes (Intl.PluralRules) with `{{count}}`.
 */
export const messages = {
  unpublishedChanges: 'Unpublished changes',
  unpublishedChangesTitle: 'The published version runs in production; this workflow has newer edits. Publish (Save) again or restore from History.',
  historyMenu: 'History',
  historyMenuTitle: 'Revision history — view and restore prior versions',
  historyAria: 'Workflow revision history',
  historyTitle: 'Revision history',
  historyClose: 'Close history',
  historyLoading: 'Loading revisions…',
  historyEmptyTitle: 'No revisions yet',
  historyEmptyBody: 'Every saved change to this workflow will appear here.',
  historyCurrent: 'Current',
  historyPublished: 'Published',
  historyNodeCount: '{{count}} nodes',
  historyNodeCount_one: '{{count}} node',
  historyRestore: 'Restore',
  historyRestoreConfirm: 'Replace the current version ({{headNodes}} nodes) with this one ({{nodes}} nodes)? The current version stays in history.',
  historyRestoreYes: 'Restore this version',
  historyRestoreNo: 'Keep current',
  historyRestored: 'Version restored — the canvas reloaded.',
  historyLoadFailed: 'Couldn’t load the revision history — try again.',
  historyRestoreFailed: 'Restore failed — the version was not changed. Reload and try again.',
  historyRestoredWithRemoved: 'Version restored — {{count}} node(s) referenced by past runs were removed; their run history keeps the events.',
  proposalBannerAria: 'Agent workflow proposal',
  proposalPendingChip: 'Proposed by an agent',
  proposalPendingTitle: 'This draft is awaiting your review — approve to run it.',
  proposalAgentNote: "Agent's description",
  proposalOpenConversation: 'Open the conversation',
  proposalNotePlaceholder: 'Add a note (optional)…',
  proposalApproveRun: 'Approve & run',
  proposalReject: 'Reject',
  proposalApproved: 'Approved — the run has started.',
  proposalViewRun: 'View run',
  proposalRejected: 'Rejected — the draft was archived.',
  proposalStaleError: 'The draft changed while deciding — review and approve again.',
  proposalAlreadyDecided: 'This proposal was already decided on another surface.',
  proposalExpiredError: 'This proposal expired — ask the agent to propose again.',
  // ── Defaults / workflow naming ──────────────────────────────────────────
  untitledWorkflow: 'Untitled workflow',
  importedWorkflow: 'Imported workflow',
  workflowNameSuffixImported: '{{name}} (imported)',
  workflowNameSuffixCopy: '{{name}} (copy)',

  // ── Toolbar ─────────────────────────────────────────────────────────────
  backToWorkflows: '‹ Workflows',
  backToWorkflowsTitle: 'Back to workflows',
  workflowNamePlaceholder: 'Workflow name',
  workflowIdTitle: 'Workflow ID',
  historyGroup: 'Undo and redo',
  share: 'Share',
  workflowMenu: 'Workflow',
  largeCanvasNote: 'Large workflow ({{count}} nodes) — graphs past ~{{budget}} nodes can slow the canvas. Consider splitting sections into sub-workflows (a workflow can hold workflows).',
  largeCanvasDismiss: 'Got it',
  publishChanges: 'Publish changes',
  publishChangesTitle: 'Production runs the published version; this publishes your newer edits to it.',
  publishChangesDone: '“{{name}}” published',
  publishChangesFailed: 'Publish failed. Try again.',
  undo: 'Undo',
  redo: 'Redo',
  export: 'Export',
  exportTitle: 'Export this workflow as portable JSON',
  exportChainPack: 'Export chain pack',
  exportChainPackTitle: 'Export as a workflow-chain-pack manifest (submit via the registry PR flow)',
  exportChainPackNoVersion: "Could not export this chain pack: the installed version of {{types}} isn't known, and a published chain must name exact node versions. Check these node types are installed on this host, reload, and try again.",
  exportChainPackCatalogUnavailable: "Could not export this chain pack: the node catalog didn't load from this host, so node versions can't be pinned. Check your connection and try again.",
  publishToRegistry: 'Publish to registry…',
  publishToRegistryTitle: 'Publish this workflow as a chain pack on packs.openwop.dev (walks through the PR-based submission flow)',
  import: 'Import',
  importTitle: 'Import a workflow from JSON (opens as a new workflow)',
  new: 'New',
  validate: 'Validate',
  validateTitle: 'Check the workflow for cycles, invalid inputs, and missing host capabilities — without running it',
  run: 'Run',
  running: 'Running…',

  // ── Multiplayer session (ADR 0481) ──────────────────────────────────────
  collabGoLive: 'Go live',
  collabLeave: 'Leave session',
  collabGoLiveTitle: 'Start a live co-editing session — collaborators can join this workflow',
  collabLeaveTitle: 'Leave the live session — autosave resumes',
  collabConnecting: 'Connecting…',
  collabReconnecting: 'Reconnecting…',
  collabFailed: 'Live session not connecting — leave and try again',
  collabFailedShort: 'Not connecting',
  collabLiveSolo: 'Live',
  collabLiveWithPeers_one: 'Live · {{count}} collaborator',
  collabLiveWithPeers_other: 'Live · {{count}} collaborators',
  collabPeersAria_one: 'Live session — {{count}} collaborator here: {{names}}',
  collabPeersAria_other: 'Live session — {{count}} collaborators here: {{names}}',
  collabPeersAriaSolo: 'Live session — no collaborators here yet',
  collabNodePeersAria: 'Selected by {{names}}',
  collabRunSessionState: 'Running the last session-saved state — the live session syncs the server copy periodically.',
  collabAnnLive: 'Live session connected',
  collabAnnReconnecting: 'Live session reconnecting',
  collabAnnFailed: 'Live session failed to connect',

  // ── Validate / run results ──────────────────────────────────────────────
  defaultInputsInvalidJson: 'Default inputs is not valid JSON.',
  validNode_one: '{{count}} node',
  validNode_other: '{{count}} nodes',
  validEdge_one: '{{count}} edge',
  validEdge_other: '{{count}} edges',
  validSummary: 'Valid — {{nodes}}, {{edges}}. No cycles, default inputs parse, and the host can run every node.{{limits}}',
  hostLimitsInEffect: ' Host limits in effect: {{parts}}.',
  limitNodeExecutions: 'up to {{n}} node executions',
  limitWallClock: 'up to {{seconds}}s wall-clock',
  limitLoopIterations: 'up to {{n}} loop iterations',
  limitMaxNodeExecutionsMessage: 'Workflow has {{actual}} executable nodes; the host caps a run at {{cap}} node executions. The run will breach the engine ceiling and fail (cap.breached → HOST_CAPABILITY_MISSING).',

  // ── Pre-flight banner ───────────────────────────────────────────────────
  preflightCapsHeading_one: 'Host can\'t run {{count}} node.',
  preflightCapsHeading_other: 'Host can\'t run {{count}} nodes.',
  preflightCapsIntro_one: 'The connected host doesn\'t advertise the surface this node needs — running now will fail with',
  preflightCapsIntro_other: 'The connected host doesn\'t advertise the surfaces these nodes need — running now will fail with',
  preflightNodeNeeds: 'needs',
  preflightLimitsHeading_one: 'Workflow exceeds {{count}} advertised host limit.',
  preflightLimitsHeading_other: 'Workflow exceeds {{count}} advertised host limits.',
  runAnyway: 'Run anyway',

  // ── Run overlay banner ──────────────────────────────────────────────────
  overlayStatusRunning: 'Running',
  overlayStatusWaiting: 'Waiting on you',
  overlayWaitingHint: 'A step needs your input to continue',
  overlayWaitingLink: 'Resolve →',
  overlayWaitingLinkTitle: 'Open the run to resolve the pending step',
  overlayStatusCompleted: 'Completed',
  overlayStatusFailed: 'Failed',
  overlayDebug: 'Pin outputs & debug',
  overlayDebugTitle: 'Pin this run’s node outputs onto the canvas and debug from the failed step — no round trip through run detail.',
  overlayStatusCancelled: 'Cancelled',
  overlayDone: '{{done}} done',
  overlayDoneFailed: '{{done}} done, {{failed}} failed',
  runDetailLink: 'Run detail →',
  runDetailTitle: 'Open the full run detail — timeline, reasoning, I/O',
  dismiss: 'Dismiss',
  dismissOverlayTitle: 'Dismiss the live overlay',

  // ── Publish-help banner ─────────────────────────────────────────────────
  publishHeading: 'Publish {{slug}} to packs.openwop.dev',
  publishIntro: 'Registry submission is PR-based ({{size}} manifest). In-browser publishing is intentionally off — Ed25519 signing happens at PR-merge time by the registry maintainers, per PUBLISHING.md + spec/v1/registry-operations.md.',
  publishStepDownload: 'Download manifest.json',
  publishStepDownloadNote: '(you may want to rename {{slug}} + replace fully-bound values with {{placeholder}} placeholders before submitting).',
  publishStepForkPre: 'Fork',
  publishStepForkMid: 'and add the manifest at',
  publishStepForkSee: '— see',
  publishStepForkLink: 'existing entries',
  publishStepForkPost: 'for the directory shape.',
  publishStepValidatePre: 'Run',
  publishStepValidatePost: 'locally to validate (the 9-step gate includes pack-manifest + signature checks).',
  publishStepPrPre: 'Open a PR; the maintainers run signing + final validation at merge time, then the pack appears at',

  // ── In-memory host banner ───────────────────────────────────────────────
  demoSessionStrong: 'Anonymous demo session.',
  demoSessionBody: 'Your workflows + any BYOK keys you add are scoped to this browser session and reset after 24 hours. Nothing is shared with other visitors. Sign in to keep your work.',
  hostDurabilityUnknownStrong: 'Storage durability unknown.',
  hostDurabilityUnknownBody: "We couldn't read this host's storage configuration, so we can't tell you whether your work here is saved permanently. Treat it as unsaved until you know.",
  demoSessionPrivacyLink: 'Privacy & cookies →',
  hostNonDurableStrong: 'Data on this host is not durable.',
  hostNonDurableBody: 'This deployment stores anonymous-session data in memory — workflows and keys added without signing in do not survive a restart. Sign in to keep your work.',
  dismissNotice: 'Dismiss notice',

  // ── Workflows dashboard ─────────────────────────────────────────────────
  dashboardEyebrow: 'Build',
  dashboardTitle: 'Workflows',
  dashboardLede: 'Compose multi-step, multi-agent workflows on a visual canvas — then run them by name from chat.',
  newWorkflow: '+ New workflow',
  yourWorkflows: 'Your workflows',
  searchByNamePlaceholder: 'Search by name…',
  sortBy: 'Sort by',
  sortUpdated: 'Updated',
  sortCreated: 'Created',
  sortName: 'Name',
  sortAscendingTitle: 'Ascending — click to flip',
  sortDescendingTitle: 'Descending — click to flip',
  sortDirectionAria: 'Sort direction: {{dir}}',
  filteredOfTotal: '{{filtered}} of {{total}}',
  noWorkflowsYet: 'No workflows yet.',
  noWorkflowsYetHint: 'Click + New workflow for a blank canvas, or pick a template below.',
  noWorkflowsMatch: 'No workflows match “{{query}}”.',
  clearFilters: 'Clear filters',
  openWorkflowAria: 'Open workflow {{name}}',
  openWorkflowAction: 'Open',
  workflowActions: 'Workflow actions',
  rename: 'Rename',
  archivedFilter: 'Archived',
  draftChip: 'Draft',
  aiAuthoredChip: 'AI-authored',
  aiAuthoredChipTitle: 'A model authored this workflow. Review it before you run it.',
  saveDraft: 'Save',
  saveDraftTitle: 'Save this draft as a workflow',
  saveDraftLockedTitle: 'Run the draft successfully once to save it',
  draftPromoted: '“{{name}}” saved to your workflows',
  viewRuns: 'Runs',
  viewRunsTitle: 'See this workflow’s recent runs',
  draftPromoteFailed: 'Couldn’t save the draft — try again.',
  archivedChip: 'Archived',
  archive: 'Archive',
  unarchive: 'Unarchive',
  archivedToast: '“{{name}}” archived',
  unarchivedToast: '“{{name}}” restored',
  archiveFailed: 'Couldn’t change the archive state — try again.',
  deleteReferencedBody: '“{{name}}” has runs that replay against it — archive it instead of deleting.',
  duplicate: 'Duplicate',
  exportJson: 'Export JSON',
  assignToMenuItem: 'Assign to…',
  assignTo: 'Assign "{{name}}"',
  assignToHint: 'Add this workflow to an agent’s portfolio or a project so it can be run, scheduled, or triggered there.',
  assignTarget: 'Assign to',
  assignSelectPlaceholder: 'Choose an agent or project…',
  assignAgentsGroup: 'Agents',
  assignProjectsGroup: 'Projects',
  assignButton: 'Assign',
  assignSuccess: 'Assigned "{{name}}" to {{target}}.',
  assignAlready: '"{{name}}" is already assigned to {{target}}.',
  assignNoTargets: 'No agents or projects yet. Create an agent or project first, then assign this workflow to it.',
  deleteConfirm: 'Delete "{{name}}"? This cannot be undone.',
  nodeCount_one: '{{count}} node',
  nodeCount_other: '{{count}} nodes',
  updatedRelative: 'Updated {{time}}',
  relativeJustNow: 'just now',
  relativeMinutesAgo_one: '{{count}} min ago',
  relativeMinutesAgo_other: '{{count}} mins ago',
  relativeHoursAgo_one: '{{count}} hr ago',
  relativeHoursAgo_other: '{{count}} hrs ago',
  relativeDaysAgo_one: '{{count}} day ago',
  relativeDaysAgo_other: '{{count}} days ago',
  useTemplate: 'Use template',
  configureConnectionsCta: 'Set up connections →',

  // ── Pack-fed templates (ADR 0163 Phase 4) ───────────────────────────────
  templatesFromPacks: 'Templates',
  templatesFromPacksHint: 'Real workflows from installed packs — click Use template to add an editable copy you can run and assign.',
  templatesFromPacksEmpty: 'No workflow packs installed yet. Install one from the registry to add real, editable workflow templates.',
  installPackCta: 'Install from registry',
  installPackTitle: 'Install a workflow pack',
  installPackHint: 'Install a workflow-chain pack from the registry (packs.openwop.dev) by name and version. It is signature-verified and becomes available as templates immediately — no restart.',
  installPackName: 'Pack name',
  installPackNameHelp: 'The reverse-DNS pack id, e.g. core.openwop.workflows.market-intel.',
  installPackVersion: 'Version',
  installPackButton: 'Install',
  installPackSuccess: 'Installed {{name}} — {{count}} new template(s) available.',
  installPackAlready: '{{name}} is already installed at this version.',
  installPackForbidden: 'Installing packs requires operator access.',
  installPackNotFound: 'Pack {{name}}@{{version}} was not found in the registry.',
  installPackUnverified: 'That pack failed signature/integrity verification and was not installed.',
  installPackError: 'Couldn’t install the pack. Check the name and version and try again.',
  installPackInstalled: 'Installed packs: {{packs}}',
  categoryPack: 'Pack',
  templateChildWorkflows_one: 'This template also added {{count}} supporting workflow it runs as a sub-workflow — it appears in your list.',
  templateChildWorkflows_other: 'This template also added {{count}} supporting workflows it runs as sub-workflows — they appear in your list.',
  templateNeedsSetup: 'Added — some nodes need setup before it can run: {{nodes}}',
  templateVersionMismatch_one: 'Copied — but {{count}} step is installed at a different version than this template was built for: {{nodes}}. It may behave differently.',
  templateVersionMismatch_other: 'Copied — but {{count}} steps are installed at different versions than this template was built for: {{nodes}}. They may behave differently.',
  templateVersionMismatchItem: '{{label}} ({{installed}} installed, template uses {{pinned}})',
  templateIncompleteConfig: 'Copied — but {{count}} step still needs setup: {{fields}}. Open the step to fill it in.',
  templateUnfilledParams_one: 'Copied — but {{count}} template input is still blank: {{params}}. A run will fail until you fill it in.',
  templateUnfilledParams_other: 'Copied — but {{count}} template inputs are still blank: {{params}}. A run will fail until you fill them in.',
  templateInstantiateError: 'Could not create a workflow from this template. Please try again.',

  // ── Node palette ────────────────────────────────────────────────────────
  paletteCategoryFlow: 'Flow',
  paletteCategoryData: 'Data',
  paletteCategoryAi: 'AI',
  paletteCategoryControl: 'Control',
  paletteCategoryJourney: 'Journey',
  paletteCategoryIntegration: 'Integration',
  paletteBuiltin: 'Built-in',
  nodes: 'Nodes',
  registry: 'Registry',
  registryTitle: 'Browse all published packs in the live registry',
  dragOntoCanvas: 'Drag onto the canvas.',
  searchNodesPlaceholder: 'Search nodes…',
  clearSearch: 'Clear search',
  matchCount_one: '{{count}} match',
  matchCount_other: '{{count}} matches',
  noNodesMatch: 'No nodes match.',
  hostLocalNodesTitle: 'Host-local nodes',
  paletteItemBlockedTitle: 'Needs host capability: {{caps}}.\nDragging works, but execution will fail with HOST_CAPABILITY_MISSING.',
  paletteItemHostWarn: 'host?',
  paletteItemHostWarnAria: 'Needs host capability {{caps}}',
  paletteItemAddAria: 'Add {{label}} to the canvas',

  // ── Canvas (a11y + small-screen) ────────────────────────────────────────
  canvasAria:
    'Interactive workflow canvas — drag nodes from the palette, connect ports, and use keyboard shortcuts to copy, paste, duplicate, or delete the selection',
  canvasMobileHint:
    'The workflow canvas is best used on a larger screen — it still works here, but a wider viewport gives you room to wire nodes.',
  canvasShortcuts:
    'Keyboard: ⌘/Ctrl+C copy · ⌘/Ctrl+V paste · ⌘/Ctrl+D duplicate · Delete removes the selection · / finds a node · Alt+Arrows move between nodes · Alt+Shift+Arrows extend the selection.',
  canvasKeyboardShortcuts: 'Keyboard shortcuts',
  canvasConnectHint: 'Connect this node…',

  // ── Inspector (node) ────────────────────────────────────────────────────
  unknownNodeKind: 'Unknown node kind: {{kind}}',
  hostCapabilityMissingAria: 'Host capability missing',
  needsHostCapability: 'Needs host capability:',
  hostCapabilityHelp: 'This engine doesn\'t advertise the required surface. The node will still serialize and ship in the workflow, but running it here returns',
  hostCapabilityHelpAfter: '. Wire the surface in your host, or run',
  hostCapabilityHelpExample: 'for a host that advertises every surface.',
  modelCapabilityRequirementsAria: 'Model capability requirements',
  requiresModelCapabilities: 'Requires model capabilities:',
  discoveringModelCapabilitiesPre: 'Discovering host\'s',
  discoveringModelCapabilitiesPost: 'advertisement…',
  // Quiet covered-state confirmation (shown inline, not as an alert box).
  modelCapsCoveredNote: 'supported by this host — dispatches directly.',
  modelCapabilitiesGapPre: 'The host\'s',
  modelCapabilitiesGapMid: 'doesn\'t cover',
  modelCapabilitiesGapPost: '. At dispatch time the host will either substitute a fallback model',
  modelCapabilitiesGapOr: 'or refuse with',
  fieldName: 'Name',
  fieldType: 'Type',
  configuration: 'Configuration',
  outputRole: 'Output role',
  outputRoleArtifact: 'Artifact',
  outputRoleTitle: 'Author hint for which terminal node\'s output is the workflow\'s canonical deliverable. Advisory; engine ignores the value.',
  outputRoleNone: '(none)',
  outputRolePrimary: 'Primary',
  outputRoleSecondary: 'Secondary',
  outputRoleNote: 'Tag the canonical-deliverable terminal node so the chat\'s completion card surfaces this output as the workflow\'s primary artifact. Advisory; the engine ignores the value.',
  presetInputs: 'Preset inputs',
  presetInputsNote: "Values the workflow's author set on this node's input ports. They determine what the node sends. Edit or clear them here — a cleared value stays cleared.",
  presetInputOverrides: ' — overrides the incoming edge',
  presetInputEmpty: 'empty',
  presetInputValueLabel: 'Value for the “{{port}}” input',
  presetInputClear: 'Clear',
  presetInputNotEditable: '(reference — edit it where it is defined)',
  deleteNode: 'Delete node',
  // ── Node connections (keyboard edge management; DESIGN.md §11 / BLD-1) ─────
  connectionsSection: 'Connections',
  noConnections: 'No connections yet.',
  connectionOut: 'Output {{port}} to {{node}} ({{targetPort}})',
  connectionIn: 'Input {{targetPort}} from {{node}} ({{port}})',
  removeConnection: 'Remove connection: {{desc}}',
  noCompatibleTargets: 'No compatible target nodes on the canvas yet.',
  connectFromOutput: 'From output',
  connectToNode: 'To node',
  connectPickNode: 'Choose a node…',
  connectToInput: 'To input',
  connectionExists: 'This connection already exists.',
  connectionAdded: 'Connected {{source}} to {{target}}.',
  addConnection: 'Add connection',

  // ── Inspector (workflow) ────────────────────────────────────────────────
  workflow: 'Workflow',
  workflowInspectorDesc: 'Click a node to edit it. These fields apply when no node is selected.',
  workflowName: 'Workflow name',
  workflowId: 'Workflow ID',
  defaultInputsLabel: 'Default inputs (JSON)',
  defaultInputsHelpPre: 'Passed to the first node as',
  defaultInputsHelpPost: 'when this workflow runs.',
  inputSchemaSection: 'Run inputs',
  inputSchemaLabel: 'Input schema (JSON Schema)',
  inputSchemaHelp: 'Describes the form shown when this workflow is launched. Leave empty for free-form JSON inputs.',
  inputSchemaInvalid: 'Not valid JSON yet — the draft is kept, but nothing is published until it parses.',
  inputSchemaPreview: 'Launch form preview',
  inputSchemaNotRenderable: 'Valid JSON, but not a renderable object schema — launches will fall back to free-form JSON.',

  // ── Inspector (edge) ────────────────────────────────────────────────────
  edge: 'Edge',
  edgeInspectorDesc: 'Edges connect node outputs to downstream inputs. The trigger rule controls fan-in behavior when a target has multiple incoming edges.',
  fromTo: 'From → To',
  edgeLabel: 'Label (optional)',
  edgeLabelPlaceholder: 'e.g. \'on success\', \'high confidence\'',
  triggerRule: 'Trigger rule',
  triggerRuleAppliesTo: 'Applies to target',
  triggerRuleAppliesToAfter: '. When multiple edges target the same node, all should declare the same rule (the scheduler picks the rule from the lexicographically-first edge id if they diverge).',
  conditionPredicate: 'Condition predicate (optional)',
  conditionPath: 'Path (into source output)',
  conditionPathPlaceholder: 'e.g. \'completion\' or \'data.score\'',
  conditionPathHelp: 'When set, this edge fires only when the predicate matches the source\'s output.',
  conditionOperator: 'Operator',
  conditionValue: 'Value',
  conditionValuePlaceholder: 'literal or JSON',
  conditionValueHelpPre: 'Plain text stays a string. Values that parse as JSON',
  conditionValueHelpMid: 'are stored as the parsed value. Partial JSON',
  conditionValueHelpPost: 'silently stays a string.',
  deleteEdge: 'Delete edge',

  // Trigger-rule option help
  triggerHelpAllSuccess: 'Default. Target fires after every upstream completes successfully.',
  triggerHelpAnySuccess: 'Target fires on the first upstream success (race).',
  triggerHelpAllComplete: 'Target fires after every upstream reaches a terminal state regardless of outcome.',
  triggerHelpNoneFailed: 'Target fires when every upstream completed AND none failed.',
  triggerHelpAnyFailed: 'Target fires only when an upstream fails — error-routing path.',

  // Condition operator labels
  condOpEq: '= (equals)',
  condOpNeq: '≠ (not equal)',
  condOpTruthy: 'is truthy',
  condOpFalsy: 'is falsy',
  condOpExists: 'exists',
  condOpContains: 'contains',

  // ── Inspector (multi-select) ────────────────────────────────────────────
  nodesSelected: '{{count}} nodes selected',
  multiSelectDesc: 'Batch actions apply to every selected node. Select a single node to edit its configuration.',
  arrange: 'Arrange',
  alignLeft: 'Align left',
  alignTop: 'Align top',
  distribute: 'Distribute',
  distributeHorizontally: 'Distribute horizontally',
  distributeVertically: 'Distribute vertically',
  distributeHelp: 'Distribute evens out the gaps between three or more nodes.',
  duplicateAll: 'Duplicate all ({{count}})',
  deleteAll: 'Delete all ({{count}})',

  // ── Config inputs / pickers ─────────────────────────────────────────────
  eachLineMustMatch: '(each line MUST match: {{pattern}})',
  stringListPlaceholder: 'One per line',
  stringListOverLimit: 'Only the first {{count}} entries are kept (host limit).',
  pickProviderFirst: 'Pick a provider first…',
  noModelsForProvider: 'No models declared for {{provider}}',
  customModelIdPlaceholder: 'custom model id',
  modelListButton: 'list',
  modelListButtonTitle: 'Switch back to the declared-model dropdown',
  pickModel: 'Pick a model…',
  useRunTimeInputs: '(use run-time inputs)',
  modelRecommendedSuffix: '{{label}} (recommended)',
  modelOtherOption: 'Other… (fine-tune / snapshot / beta)',
  modelCapabilitiesAria: 'Model capabilities',
  capVision: 'Vision',
  capTools: 'Tools',
  capStructured: 'Structured',
  modelCapPillTitle: 'This model supports {{capability}}',
  pickProvider: 'Pick a provider…',
  loadingCredentials: 'Loading credentials…',
  noKeysStored: 'No {{provider}}keys stored yet.',
  credentialsLoadFailed: 'Couldn’t load your stored keys, so we can’t tell whether any are stored.',
  manageKeys: 'Manage keys',
  manageKeysToAdd: 'to add one.',
  pickKey: 'Pick a key…',
  noneOption: '(none)',
  manage: 'Manage',
  manageTitle: 'Open the Keys management page in a new tab',

  // ── Canvas / nodes ──────────────────────────────────────────────────────
  unknownNodePrefix: 'Unknown: {{kind}}',
  runStatusRunning: 'Running',
  runStatusCompleted: 'Completed',
  runStatusFailed: 'Failed',
  runStatusSuspended: 'Suspended',
  hostCantRunNodeTitle: 'This host can\'t run this node — needs: {{caps}}',
  hostCapabilityMissingNeedsAria: 'Host capability missing: needs {{caps}}',
  runStatusBadgeAria: 'Run status: {{status}}',
  emptyNote: 'Empty note — edit in the Inspector →',
  doubleClickToRename: 'Double-click to rename',

  // ── Serialize / deserialize errors ──────────────────────────────────────
  errWorkflowNoNodes: 'Workflow has no nodes.',
  errWorkflowNoExecutableNodes: 'Workflow has no executable nodes — every node is a client-only annotation (e.g. sticky notes).',
  errEdgeUnknownSource: 'Edge "{{edgeId}}" references unknown source node.',
  errEdgeUnknownTarget: 'Edge "{{edgeId}}" references unknown target node.',
  errWorkflowCycle: 'Workflow contains a cycle reaching node "{{nodeId}}".',
  errWorkflowNoSourceNodes: 'Workflow has no source nodes (every node has an incoming edge).',
  errNodeDisconnected: 'Node "{{name}}" is disconnected from every source.',
  errNodesDisconnected: 'Some nodes are disconnected from every source.',
  errUnknownNodeKind: 'Unknown node kind "{{kind}}".',
  errEdgeBackendIdResolution: 'Edge "{{edgeId}}" failed backend-id resolution.',
  errNotOpenwopExport: 'Not an OpenWOP workflow export (missing nodes/edges).',
  errDefinitionNoNodes: 'Workflow definition has no nodes.',
  errNodeMissingIdOrTypeId: 'Node {{index}} is missing an id or typeId.',
  errCantLoadNodeTypes_one: 'This host can\'t load {{count}} node type (not installed): {{types}}.',
  errCantLoadNodeTypes_other: 'This host can\'t load {{count}} node types (not installed): {{types}}.',
  loadErrorTitle: 'This workflow can\'t be opened',
  loadErrorBackToWorkflows: 'Back to workflows',
  preflightTitle: 'Use template: {{name}}',
  preflightRequirementsTitle: 'What this template needs',
  preflightConnected: 'Connected',
  preflightNotConnected: 'Not connected',
  preflightConnectCta: 'Connect',
  preflightNotOnHost: 'Not on this host',
  preflightNotOnHostHint: 'An operator can install this provider from the pack registry.',
  preflightMissingNode: 'Node type not installed',
  preflightNodeNotOnHostHint: 'An operator can install this node pack from the pack registry.',
  preflightPinMismatch: 'Different version installed',
  preflightPinMismatchHint: 'This template was built for version {{pinned}}; this host has {{installed}}. The step may behave differently. An operator can install {{pinned}} from the pack registry.',
  preflightFeatureEnabled: 'Enabled',
  preflightFeatureDisabled: 'Not enabled',
  preflightFeatureEnableCta: 'Enable',
  preflightFeaturesNotice: 'This template needs {{features}} enabled for your workspace. Enable it first — otherwise the steps that use it fail when the workflow runs.',
  preflightZeroConfig: 'Runs with zero connections — ready to go.',
  preflightInputsTitle: 'Inputs',
  preflightInputsBlurb: 'Fill these in now — they are frozen into the workflow when you copy it, and you can edit them in the builder afterwards.',
  preflightConfirm: 'Add to my workflows',
  preflightCreating: 'Adding…',
  preflightApprovalGates_one: '{{count}} step waits for your approval before anything external happens.',
  preflightApprovalGates_other: '{{count}} steps wait for your approval before anything external happens.',
  templateAsksQuestions_one: 'Asks {{count}} question',
  templateAsksQuestions_other: 'Asks {{count}} questions',
  aliasHint: 'Includes generic steps that work with {{brand}} through a connection.',
  startHereTitle: 'Start here',
  startHereHint: 'These run with zero connections — try one now.',
  allTemplatesTitle: 'All templates',
  zeroConnectionsChip: 'Zero connections',
  templateCategoryAll: 'All categories',
  templateCategoryFilterLabel: 'Filter templates by category',
  preRunConnsHeading_one: '{{count}} connection to set up.',
  preRunConnsHeading_other: '{{count}} connections to set up.',
  preRunConnsIntro: 'This workflow names connections you haven\'t linked yet — those steps will skip until you connect:',
  preRunConnecting: 'Opening consent…',
  preRunConnectError: 'Could not start the consent flow — configure it under Access & connections.',
  inspectorConnectCta: 'Connect',
  inspectorConnectError: 'Could not start the consent flow — configure it under Access & connections.',

  // ── Create with AI (ADR 0072 / 0073) — the embedded Workflow Architect chat ─
  createWithAi: 'Create with AI',
  createWithAiTitle: 'Author a workflow from a plain-language description',
  aiPanelHeading: 'Create a workflow with AI',
  aiPanelHint: 'Describe what you want to automate. The Workflow Architect plans a workflow using only the nodes this host supports, registers it, and gives you a link to open it on the canvas.',
  aiAuthoring: 'Authoring…',
  aiAuthoredReady_one: 'A new workflow appeared in this workspace since you opened this panel.',
  aiAuthoredReady_other: '{{count}} new workflows appeared in this workspace since you opened this panel.',
  aiOpenOnCanvas: 'Open {{name}} on the canvas',
  aiClose: 'Close',

  // ── Create-with-AI empty state (builder embed) ──────────────────────────────
  // Context-aware welcome shown before the first message — authoring-focused,
  // distinct from the chat page's run/agents welcome. Explains how AI authoring
  // works, then offers example intents that seed the composer.
  aiWelcomeTitle: 'Build a workflow by describing it',
  aiWelcomeBody: 'Tell the Workflow Architect what you want to automate in plain language. It reads the nodes this host supports, assembles a validated, acyclic workflow, and registers it — then you open it on the canvas to review and run. No manual wiring.',
  aiWelcomeExamplesLabel: 'Start from an example',
  aiWelcomeFootnote: 'The Architect only uses nodes available here, and never runs a workflow without you.',
  aiExLeadTitle: 'Route new leads',
  aiExLeadText: 'When a new lead comes in, score it, then notify sales for hot leads and add the rest to a nurture list.',
  aiExInvoiceTitle: 'Approve invoices',
  aiExInvoiceText: 'Extract the total from an incoming invoice and send it for approval; auto-approve anything under $500.',
  aiExDocTitle: 'Summarize documents',
  aiExDocText: 'Take an uploaded document, summarize it, and email the summary to the requester.',
  aiSeedPrompt: 'Create a workflow that performs these steps in order: {{steps}}. Goal: {{goal}}.',
  // §7.2/§7.4/§7.5 canvas convergence (CV-1/2/5/17)
  moreActions: 'More actions',
  commandsGroup: 'Workflow builder',
  paletteRail: 'Node palette rail',
  inspectorRail: 'Inspector rail',
  collapsePalette: 'Collapse the palette rail',
  expandPalette: 'Expand the palette rail',
  collapseInspector: 'Collapse the inspector rail',
  expandInspector: 'Expand the inspector rail',
  resizePalette: 'Resize the palette rail',
  resizeInspector: 'Resize the inspector rail',
  annPaletteCollapsed: 'Palette rail collapsed',
  annPaletteExpanded: 'Palette rail expanded',
  annInspectorCollapsed: 'Inspector rail collapsed',
  annInspectorExpanded: 'Inspector rail expanded',
  shortcutCopyNodes: 'Copy selected nodes',
  shortcutPasteNodes: 'Paste nodes',
  shortcutDuplicateNodes: 'Duplicate selected nodes',
  shortcutFindNode: 'Find a node (search the palette)',
  annFindNodeFocused: 'Node search focused.',
  shortcutNodeNavUpstream: 'Select upstream node',
  shortcutNodeNavDownstream: 'Select downstream node',
  shortcutNodeNavUp: 'Select node above',
  shortcutNodeNavDown: 'Select node below',
  annNodeNavSelected: '{{name}} selected.',
  // Left/right walk EDGES (upstream/downstream); up/down are spatial. One
  // "no node in that direction" would be a false claim about the graph — an
  // unconnected node can sit visibly to the right of the selection.
  // Base = i18next's documented fallback if a context ever misses. Unreachable
  // in practice (`dir` is a four-member union), so it stays deliberately vague
  // rather than asserting a direction it cannot know.
  annNodeNavNone: 'No node that way.',
  annNodeNavNone_left: 'No upstream node.',
  annNodeNavNone_right: 'No downstream node.',
  annNodeNavNone_up: 'No node above.',
  annNodeNavNone_down: 'No node below.',
  shortcutNodeExtendSelection: 'Extend selection to the next node',
  annNodeRangeSelected: '{{name}} added — {{count}} nodes selected.',
  shortcutNodeFloodDownstream: 'Select everything downstream',
  shortcutNodeFloodUpstream: 'Select everything upstream',
  annNodeFloodSelected: '{{count}} nodes selected.',
  annNodeFloodNone: 'Nothing further in that direction.',
  shortcutSelectAllNodes: 'Select all nodes',
  annAllNodesSelected: 'All {{count}} nodes selected.',
  annCopiedNodes: 'Copied {{count}} node(s)',
  annPastedNodes: 'Pasted {{count}} node(s)',
  annDuplicatedNodes: 'Duplicated {{count}} node(s)',
  connectArmFromNode: 'Start a connection from {{name}}',
  connectTargetNode: 'Connect to {{name}}',
  connectTo: 'Connect',
  connectArmed: 'Connecting from {{name}} — choose a compatible target',
  cancelConnect: 'Cancel',
  dropPickerLabel: "Add a connected node",
  dropPickerSearch: "Search nodes\u2026",
  dropPickerMore: "{{count}} more match \u2014 keep typing to narrow",
  dropPickerEmpty: "No compatible nodes",
  runDrawerLabel: 'Run inspector',
  runDrawerTitle: 'Run',
  runDrawerEmpty: 'No node activity yet',
  runDrawerPayload: 'Payload',
  runDrawerStatusRunning: 'running',
  runDrawerStatusCompleted: 'completed',
  runDrawerStatusFailed: 'failed',
  runDrawerStatusSuspended: 'waiting',
  removedReferencedTitle_one: 'A step your run history references was removed',
  removedReferencedTitle_other: '{{count}} steps your run history references were removed',
  removedReferencedBody_one: 'A previous run recorded “{{ids}}”. That run still opens normally, but a replay or fork can no longer map its recorded result to that step. Undo if you didn’t mean to remove it, or duplicate the workflow to keep the old shape.',
  removedReferencedBody_other: 'Previous runs recorded these steps: {{ids}}. Those runs still open normally, but a replay or fork can no longer map their recorded results to them. Undo if you didn’t mean to remove them, or duplicate the workflow to keep the old shape.',
  syncFailedTitle: 'This workflow isn\'t saved to your account',
  syncFailedAuth: 'Your session expired, so recent edits exist only in this browser. Sign in again to save them.',
  syncFailedRateLimited: 'Too many requests, so recent edits exist only in this browser. Wait a moment and make another edit to retry.',
  syncFailedServer: 'The server rejected the save, so recent edits exist only in this browser. Make another edit to retry.',
  syncCollabRoomLive: 'This workflow is saving through a live session. Join the session (Go live) to edit together — solo edits stay in this browser until it ends.',
  saveRefused: 'Not saved to your account — the server refused the change. Your edit is still in this browser.',
  saveRefusedAuth: 'Not saved — your session expired. Sign in again, then retry.',
  listRefusedTitle: 'Couldn\'t load your workflows',
  listRefusedAuth: 'Your session expired. Sign in again to see your workflows.',
  listRefusedRetry: 'The server refused the request. This is not an empty account — retry in a moment.',

  // ADR 0475 — the workflow debug loop (pins + execute-from-step).
  debugSessionTitle: 'Debug session',
  debugSessionPins_one: '{{count}} pinned output',
  debugSessionPins_other: '{{count}} pinned outputs',
  debugSessionSourceRun: 'View source run',
  debugSessionClear: 'Clear pins',
  debugSessionClearFailed: 'Clearing pins failed — try again.',
  debugSection: 'Debug',
  debugPinnedOutput: 'Pinned output',
  debugPinEditorLabel: 'Pinned output JSON',
  debugPinSave: 'Save pin',
  debugPinEdit: 'Edit pin',
  debugPinAdd: 'Pin output…',
  debugPinRemove: 'Unpin',
  debugPinInvalidJson: 'The pinned output must be a JSON object.',
  debugRunFromHere: 'Run from here',
  debugRunFromHereTitle: 'Run this node and everything after it, using pinned outputs for the nodes before it.',
  debugRunOnly: 'Only this node',
  debugRunOnlyTitle: 'Run just this node, using pinned outputs for the nodes before it.',
  debugMissingPins: 'Pin these upstream nodes first: {{nodes}}.',
  debugPinnedBadgeTitle: 'Output pinned for debugging',
  debugPinnedBadgeAria: 'This node\'s output is pinned for debugging',
  debugPrefillUnmatched: 'Some outputs from that run belong to nodes that no longer exist and were not pinned: {{nodes}}.',
  debugPrefillFailed: 'Couldn\'t load pins from that run. You can still pin node outputs manually.',
  debugPinsLoadFailed: 'Couldn\'t load this workflow\'s pinned outputs, so any existing pins aren\'t shown. Reload to try again — don\'t assume the session is empty.',
  debugErrSaveFirst: 'This workflow isn\'t saved to the server yet — run or save it once, then pin.',
  debugErrRateLimited: 'Too many requests right now. Wait a moment and try again.',
  debugErrRejected: 'The server refused that debug action. Check the pin and try again.',
  debugErrGeneric: 'That debug action failed. Try again in a moment.',
  debugPrefillGone: 'That workflow (or run) no longer exists on the server, so there is nothing to pin from it.',
  debugPrefillDone: 'Pinned {{count}} node output(s) from the run. Pick a node and run from there.',

  // ADR 0476 — fleet insights: dashboard band, card chips, heatmap, run estimate.
  fleetBandAria: 'Fleet run statistics',
  fleetRuns: 'Recent runs',
  fleetSuccess: 'Success rate',
  fleetP95: 'Slowest p95',
  fleetCost: 'Model cost',
  fleetSeconds: '{{s}}s',
  fleetWindowNote: 'Over the last {{rows}} runs (since {{since}}). Not all-time totals.',
  statsSuccess: '{{pct}}% success',
  statsSuccessTitle: 'Completed vs failed runs in the recent-runs window (cancellations excluded).',
  statsP95: 'p95 {{s}}s',
  statsP95Title: '95th-percentile duration of completed runs in the window.',
  statsCostTitle: 'Total recorded model cost in the window.',
  statsHotspot: 'fails at {{node}} ({{count}})',
  statsHotspotTitle: 'The node where failed runs most recently stopped, and how often.',
  heatmapMenuOn: 'Show failure heatmap',
  heatmapMenuOff: 'Hide failure heatmap',
  heatmapMenuTitle: 'Badge each node with how many recent runs failed there.',
  heatmapNoFailures: 'No recorded failures for this workflow in the recent-runs window.',
  runEstimateTipAria: 'About this cost estimate',
  fleetStatsUnavailable: 'Run statistics are unavailable right now — the workflows below are unaffected.',
  statsHotspotAnonymous_one: '{{count}} recent run failed at a removed or renamed step',
  statsHotspotAnonymous_other: '{{count}} recent runs failed at a removed or renamed step',
  heatmapLoadFailed: 'Couldn\'t load failure stats. Try again in a moment.',
  heatBadgeTitle_one: '{{count}} recent run failed at this node',
  heatBadgeTitle_other: '{{count}} recent runs failed at this node',
  heatBadgeAria_one: '{{count}} recent run failed at this node',
  heatBadgeAria_other: '{{count}} recent runs failed at this node',
  runEstimateChip: '~{{usd}}/run',
  runEstimateUnavailableChip: 'Cost estimate unavailable',
  runEstimateUnavailableTitle: 'The run-cost estimate could not be loaded, so the cost of this run is unknown rather than zero.',
  runEstimateHistoricalTitle_one: 'Median recorded model cost of this workflow\'s recent runs ({{samples}} sample). Not a quote.',
  runEstimateHistoricalTitle_other: 'Median recorded model cost of this workflow\'s recent runs ({{samples}} samples). Not a quote.',
  runEstimateStaticTitle_one: 'A rough floor from {{count}} AI step at current model rates (~1K tokens in/out per step). Not a quote.',
  runEstimateStaticTitle_other: 'A rough floor from {{count}} AI steps at current model rates (~1K tokens in/out per step). Not a quote.',

  // ADR 0482 — cost heatmap mode + per-workflow daily budgets.
  costHeatmapMenuOn: 'Show cost heatmap',
  costHeatmapMenuOff: 'Hide cost heatmap',
  costHeatmapMenuTitle: 'Badge each node with what it cost in this workflow\'s latest finished run.',
  costHeatmapNoData: 'No finished run with recorded per-node cost yet — run the workflow first.',
  costHeatmapNoOverlap: 'The latest run\'s cost is attributed to nodes that are no longer on this canvas.',
  costHeatChip: 'Cost: latest run',
  costHeatChipTitle: 'Node badges show the latest finished run\'s recorded model cost per node — one run, not a trend.',
  costBadgeTitle: 'This node cost {{usd}} in the latest finished run',
  costBadgeAria: 'This node cost {{usd}} in the latest finished run',
  setBudgetMenuItem: 'Set budget…',
  setBudgetTitle: 'Daily budget for {{name}}',
  setBudgetHint: 'Alerts at 80% and 100% of the daily amount. Spend counts every run — including debug and eval runs.',
  budgetDailyLabel: 'Daily budget (USD)',
  budgetDailyHelp: 'Recorded model cost per calendar day (UTC).',
  budgetDailyPlaceholder: 'e.g. 5.00',
  budgetHardCapLabel: 'Hard cap: block new runs once the budget is spent',
  budgetHardCapHint: 'Blocks new production runs until the next UTC day. Debug and eval runs stay available, and running work is never interrupted. Best-effort: if today\'s spend can\'t be read, runs are allowed rather than blocked.',
  budgetSave: 'Save budget',
  budgetRemove: 'Remove budget',
  budgetSaved: 'Budget saved for {{name}}.',
  budgetCleared: 'Budget removed for {{name}}.',
  budgetSaveFailed: 'Couldn\'t save the budget. Try again in a moment.',
  budgetInvalidAmount: 'Enter a daily amount greater than zero.',
  budgetChip: '{{spent}} / {{budget}} today',
  budgetChipTitle: 'Today\'s recorded model spend vs the daily budget ({{pct}}%). Includes debug/eval spend; best-effort estimates, not billing. Counts spend so far today and updates when runs finish.',
  budgetChipHardCapTitle: 'Hard cap on: new runs are blocked once the budget is spent.',
  budgetChipTipAria: 'About this budget',
  budgetChipCappedAria: 'capped',
  budgetSpentToday: 'Spent today: {{amount}}',
  budgetSaveFailed400: 'The server rejected this amount. Enter a daily amount greater than zero.',
  budgetSaveFailed404: 'This workflow no longer exists on the server — refresh the workflows list.',
  costHeatChipTipAria: 'About the cost view',
  failureHeatChip: 'Failures: recent runs',
  failureHeatChipTitle: 'Node badges count how many recent runs failed at each node — the recent-runs window, not all time.',
  failureHeatChipTipAria: 'About the failure view',

  // ADR 0477 — workflow evaluations.
  evalsMenu: 'Evaluations…',
  evalsMenuTitle: 'Named test suites for this workflow: fixtures, assertions, and an optional publish gate.',
  evalsTitle: 'Evaluations',
  evalsAria: 'Workflow evaluations',
  evalsClose: 'Close evaluations',
  evalsNewSet: 'New set…',
  evalsLoading: 'Loading evaluation sets…',
  evalsEmptyTitle: 'No evaluation sets yet',
  evalsEmptyBody: 'An evaluation set runs this workflow against fixed inputs and checks the results — like tests for code. Create one to catch regressions before publishing.',
  evalsLoadFailed: 'Couldn\'t load evaluations. Try again in a moment.',
  evalsRunFailed: 'Couldn\'t start the evaluation run. Try again in a moment.',
  evalsSaveFailed: 'Couldn\'t save the set. Try again in a moment.',
  evalsInvalidJson: 'The set is not valid JSON.',
  evalsDeleteFailed: 'Couldn\'t delete the set. Try again in a moment.',
  evalsNeverRun: 'Never run',
  evalsRunning: 'Running…',
  evalsIncomplete: 'Incomplete',
  evalsIncompleteTitle: 'The invocation was interrupted (for example a server restart) before every case settled. Run it again.',
  onlineLoading: 'Loading online results…',
  onlineLoadFailed: 'Couldn’t load online results.',
  onlineEmpty: 'Online scoring is on ({{pct}}% sample) — no production runs scored yet.',
  onlinePass7d: '{{pct}}% pass · last 7 days ({{count}} runs)',
  onlineChip: 'Online',
  onlineChipTitle: 'Online scoring is on — {{pct}}% of production runs are scored against this set’s invariants.',
  onlineFailureLinkAria: 'Open failing run {{runId}} from {{date}} — failed: {{kinds}}',
  onlineFailureRunFailed: 'run failed',
  onlineNoRecent: 'No runs scored in the last 7 days',
  onlineSparkAria_one: 'Daily pass rate across the most recent day with scored runs',
  onlineSparkAria_other: 'Daily pass rate across the {{count}} most recent days with scored runs',
  onlineWindowNote_one: 'Last {{count}} active day · {{pct}}% of production runs sampled. Days without scored runs are not shown; not all-time totals.',
  onlineWindowNote_other: 'Last {{count}} active days · {{pct}}% of production runs sampled. Days without scored runs are not shown; not all-time totals.',
  onlineJudgeSkipped_one: 'Judge skipped on {{count}} run (daily judge budget, or the run didn’t complete) — its deterministic checks still scored and the judge verdict was EXCLUDED, never failed.',
  onlineJudgeSkipped_other: 'Judge skipped on {{count}} runs (daily judge budget, or runs didn’t complete) — their deterministic checks still scored and judge verdicts were EXCLUDED, never failed.',
  onlineRecentFailures: 'Recent failing runs:',
  evalsGreen_one: '{{count}} case green',
  evalsGreen_other: 'All {{count}} cases green',
  evalsRed: '{{failed}} of {{total}} failed',
  evalsCaseCount_one: '{{count}} case',
  evalsCaseCount_other: '{{count}} cases',
  evalsGateChip: 'Publish gate',
  evalsGateChipTitle: 'Saving this workflow requires the latest run of this set to be green for the current draft.',
  evalsRunNow: 'Run now',
  evalsEdit: 'Edit',
  evalsShowDetail: 'Show results',
  evalsHideDetail: 'Hide results',
  evalsDelete: 'Delete',
  evalsDeleteConfirm: 'Delete this set and its results?',
  evalsDeleteYes: 'Delete',
  evalsDeleteNo: 'Keep',
  evalsEditorLabel: 'Evaluation set JSON',
  evalsSaveSet: 'Save set',
  evalsCase_passed: 'Passed',
  evalsCase_failed: 'Failed',
  evalsCase_running: 'Running',
  evalsCase_timed_out: 'Timed out',
  evalsOpenRun: 'Open run',
  evalsAssertionFailed: '{{kind}}: {{detail}}',
  evalsDetailAria: 'Latest results for {{name}}',
  evalsGreenUnverified: 'Green — freshness unverified',
  evalsGreenUnverifiedTitle: 'These cases passed, but the workflow’s current revision could not be read, so this result cannot be confirmed as current. The publish gate may still refuse it as stale.',
  evalsGreenStale: 'Green — older revision',
  evalsGreenStaleTitle: 'This green result predates your latest edits, so the publish gate will not accept it. Run the set again.',
  evalsRunNowTitle: 'Runs the last-saved draft (recent edits sync within a couple of seconds).',
  evalsWorkflowUnsynced: 'This draft hasn\'t synced to the server yet — wait a moment (or run it once), then save the set again.',
  saveDraftEvalsFailing: 'A required evaluation set is not green for this draft. Run it (and pass) before saving.',
  // ADR 0770 — builder integrity (load/sync honesty, gallery search).
  loadRefused: 'The server didn\'t return this workflow, so it hasn\'t been opened. Nothing was changed. Try again, or check that you\'re signed in.',
  loadOffline: 'You\'re offline and this device has no copy of this workflow. Reconnect, then try again.',
  loadErrorRetry: 'Try again',
  loadingWorkflow: 'Opening workflow…',
  syncConflictTitle: 'This workflow was saved somewhere else.',
  syncConflictBody: 'Someone (or another tab) saved a newer version after you opened it. Load latest replaces your edits in this browser with that version. Keep mine replaces that version with your edits.',
  syncConflictReload: 'Load latest',
  syncConflictKeepMine: 'Keep mine',
  syncFailedReadOnly: 'You have view-only access to this workspace, so your edits stay in this browser. Ask a workspace admin for edit access.',
  templateReusedChildWorkflows_one: '{{count}} supporting workflow you had already edited was kept as you left it.',
  templateReusedChildWorkflows_other: '{{count}} supporting workflows you had already edited were kept as you left them.',
  templateInstantiateReadOnly: 'You have view-only access to this workspace, so you can\'t add workflows to it.',
  templateInstantiateDisabledPack: 'This template uses a pack that is turned off for this workspace. Turn the pack on, then try again.',
  deleteReadOnly: 'You have view-only access to this workspace, so you can\'t delete workflows.',
  deleteFailed: '"{{name}}" wasn\'t deleted. Try again.',
  searchWorkflowsLabel: 'Search your workflows',
  searchTemplatesPlaceholder: 'Search templates',
  searchTemplatesLabel: 'Search templates',
  templatesLoadErrorTitle: 'Templates didn\'t load',
  templatesLoadErrorBody: 'The template list couldn\'t be read from the server. This doesn\'t mean none are installed.',
  templatesNoMatchTitle: 'No templates match',
  templatesClearFilters: 'Clear search and filter',
  useTemplateNamed: 'Use template: {{name}}',
  runRefusedReadOnly: 'You have view-only access to this workspace, so this workflow can\'t be saved or run from here.',
  runRefusedDisabledPack: 'This workflow uses a pack that is turned off for this workspace. Turn the pack on in the Marketplace, then run it again.',
  runRefusedGeneric: 'The workflow couldn\'t be saved before running, so the run didn\'t start. Try again.',
  syncFlushFailed: 'Your last edits to "{{name}}" didn\'t save to the server. They are still in this browser; open that workflow again to retry.',
  forkedSharedWorkflow: 'This is a shared template, so your edits were saved to your own copy. The original stays unchanged for everyone else.',
  forkRepointFailed: 'Some schedules or board cards linked to this workflow could not be moved to your copy and still run the original. Re-link them to your copy.',
  // ADR 0773 — Builder Pair Canvas.
  pairRailLabel: 'Pair and Inspect rail',
  pairRailTabsAria: 'Right rail',
  pairTabPair: 'Pair',
  pairTabInspect: 'Inspect',
  pairPendingAria_one: '{{count}} suggested change waiting for review',
  pairPendingAria_other: '{{count}} suggested changes waiting for review',
  pairAskAbout: 'Ask about {{name}}',
  pairAskAnything: 'Ask the Workflow Architect',
  pairAskSend: 'Send to the Workflow Architect',
  pairBlockedSync: 'Your latest edits aren’t saved yet, so the Architect would read an older version. Resolve the save problem above, then send again.',
  pairBlockedNotFound: 'This workflow isn’t saved to your workspace yet. Save it once, then ask again.',
  pairBlockedNoWorkflow: 'Open or create a workflow before asking about it.',
  pairBlockedContext: 'Couldn’t share the canvas with the Architect. Nothing was sent. Try again.',
  pairAskAddKind: 'Where should a “{{label}}” step go in this workflow? Suggest it on the canvas.',
  pairCanSee: '“{{name}}” as it is now (unsaved edits included), your selection, and the node catalog',
  pairCanSeeLive: 'live session: reads the last shared snapshot',
  pairCanSeeRun: 'the run you pointed at',
  pairRoomShared_one: '{{count}} suggestion is shared with this session by a collaborator.',
  pairRoomShared_other: '{{count}} suggestions are shared with this session by collaborators.',
  pairLoadFailed: 'Couldn’t load the latest suggestion. It will retry after the next reply.',
  pairContextAria: 'What your next message is about',
  pairContextNone: 'Nothing selected: the Architect considers the whole workflow.',
  pairUncite: 'Stop citing {{name}}',
  pairChipEdge: 'Selected edge',
  pairChipRun: 'Failed run',
  pairCitePicker: 'Cite another node',
  pairCiteNode: '+ node',
  pairContextFollows: 'follows your selection',
  pairPhase_thinking: 'Thinking',
  pairPhase_reading: 'Reading the canvas',
  pairPhase_drafting: 'Drawing a suggestion',
  pairPhase_review: 'Ready to review',
  pairOpPending: 'Pending',
  pairOpKept: 'Kept',
  pairOpDropped: 'Dropped',
  pairOpSuperseded: 'Replaced',
  pairOpStale: 'Changed since',
  pairOpInapplicable: 'Needs a dropped change',
  pairAnnKept_one: 'Kept {{count}} change. Undo with ⌘Z.',
  pairAnnKept_other: 'Kept {{count}} changes. Undo with ⌘Z.',
  pairAnnKeptSkipped: 'Kept {{kept}}; {{skipped}} could not be kept because the canvas changed.',
  pairAnnDropped: 'Change dropped.',
  pairAnnDroppedAll: 'Suggestion dropped. Nothing changed.',
  pairReviewAria: 'Suggested changes',
  pairReviewTitle_one: 'Suggestion · {{count}} change',
  pairReviewTitle_other: 'Suggestion · {{count}} changes',
  pairReviewPending: '{{count}} to review',
  pairOpGate: 'Approval gate',
  pairOpGateTitle: 'Touches an approval gate. It is never kept automatically.',
  pairDropOpAria: 'Drop change {{n}}',
  pairKeepOpAria: 'Keep change {{n}}',
  pairDrop: 'Drop',
  pairKeep: 'Keep',
  pairKeepTheirs: 'Keep current',
  pairAskRedo: 'Ask to redo',
  pairRedoPrompt: 'Change {{n}} (“{{summary}}”) no longer applies because the canvas changed. Read the canvas again and redo just that change.',
  pairAutoKeep: 'Auto-keep small edits to my selection',
  pairShareRoom: 'Share with collaborators',
  pairDropAll: 'Drop',
  pairKeepAll: 'Keep all',
  pairInstrumentAria: 'Suggestion review',
  pairInstrumentTitle: 'Architect’s suggestion',
  pairInstrumentCount_one: '{{count}} change · nothing saved yet',
  pairInstrumentCount_other: '{{count}} changes · nothing saved yet',
  pairPrevChange: 'Previous change (K)',
  pairNextChange: 'Next change (J)',
  pairGhostAria: 'Suggested change {{n}}: add {{name}}. Select to review.',
  pairGhostNew: 'New',
  pairFirstDraftTitle: 'What should this workflow do?',
  pairFirstDraftAria: 'Draft this workflow with the Architect',
  pairFirstDraftPlaceholder: 'e.g. When a lead fills in the demo form, enrich the company, score it, and put anything promising in the CRM.',
  pairFirstDraftHint: 'It’s drawn on the canvas. Nothing is saved until you keep it.',
  pairFirstDraftCta: 'Draft it',
  pairFirstDraftOrDrag: 'Or drag any node from the left rail.',
  pairFirstDraftPrompt: 'Draft this workflow on the canvas: {{intent}}',
  pairPhasesAria: 'Drafting progress',
  pairNodeToolbarAria: 'Pair actions for {{name}}',
  pairAsk: 'Ask',
  pairExplain: 'Explain',
  pairExplainPrompt: 'Explain what “{{name}}” does in this workflow and how its settings affect the run.',
  pairDiagnose: 'Diagnose',
  pairDiagnosePrompt: 'The last run failed at “{{name}}”. Diagnose why, and suggest the smallest fix on the canvas.',
  pairNodeMarksAria: 'Suggested changes {{list}} touch this node',
  pairDiffAria: 'Suggested changes to this item',
  pairDiffRemoves: 'Removes this from the workflow.',
  pairDiffStale: 'Someone changed this since it was suggested. The struck value is what’s there now.',
  pairFill: 'Fill with AI',
  pairFillAria: 'Ask the Architect to fill {{field}}',
  pairFillPrompt: 'Suggest a value for “{{field}}” on “{{name}}”, and say why.',
  pairEdgeAskLabel: 'Ask about this connection',
  pairEdgeFraming: 'About the connection from “{{from}}” to “{{to}}”:',
  pairEdgeAskHint: 'For example “only when the score is high”, or “insert an approval here”.',
  pairPaletteDescribe: 'Describe it to the Architect',
  pairPaletteDescribePrompt: 'Add a step that does this: {{query}}. Suggest it on the canvas.',
  shortcutPairNext: 'Next suggested change',
  shortcutPairPrev: 'Previous suggested change',
  shortcutPairKeep: 'Keep the focused change',
  shortcutPairDrop: 'Drop the focused change',
  shortcutPairKeepAll: 'Keep all suggested changes',
  shortcutPairAsk: 'Ask the Workflow Architect',
  // ADR 0773 P6–P9 — gap closure.
  pairAutoKept_one: 'Kept {{count}} small edit to your selection.',
  pairAutoKept_other: 'Kept {{count}} small edits to your selection.',
  pairUndo: 'Undo',
  pairUndoStale: 'That keep can’t be undone from here any more — use ⌘Z.',
  pairInlineDrawn_one: 'Drew {{count}} change.',
  pairInlineDrawn_other: 'Drew {{count}} changes.',
  pairNarrAdded: 'added {{name}}',
  pairNarrRenamed: 'renamed {{from}} → {{to}}',
  pairNarrConfigured: 'changed {{name}} settings',
  pairNarrRemoved: 'removed {{name}}',
  pairNarrConnected: 'connected {{from}} → {{to}}',
  pairNarrCondition: 'changed the condition on {{from}} → {{to}}',
  pairNarrDisconnected: 'disconnected {{from}} → {{to}}',
  pairNarrLine: 'You {{changes}}',
  pairNarrMore_one: '{{count}} more change',
  pairNarrMore_other: '{{count}} more changes',
  pairNarrationAria: 'Your edits since the last message',
  pairFollowUpsAria: 'Suggested follow-ups',
  pairDigestAria: 'The failed run',
  pairDigestTitle: 'Run failed at “{{name}}”',
  pairRestore: 'Restore',
  pairUseAi: 'Use the AI’s',
  pairUseAiConfirmTitle: 'Replace the current value with the AI’s?',
  pairUseAiConfirmBody: '“{{summary}}” — the value was changed since this was suggested. Keeping the AI’s overwrites that change (you can undo).',
  pairKeepRerun: 'Keep and re-run from here',
  pairRerunStarted: 'Kept the fix and re-ran from the failed step.',
  pairRerunNotSaved: 'The fix is kept but not saved yet, so nothing re-ran. Resolve the save problem, then run.',
  pairRerunFailed: 'The fix is kept, but the re-run didn’t start.',
  pairPlacing: 'placing {{n}} of {{total}}',
  pairStatusPending_one: 'suggestion: {{count}} pending',
  pairStatusPending_other: 'suggestion: {{count}} pending',
  pairStatusReview: 'to review',
  pairEditValue: 'Edit before keeping',
  pairKeepEdited: 'Keep edited',
  pairDiffStaleBy: '{{name}} changed this since it was suggested. The struck value is what’s there now.',
  pairRetryStep: 'Retry step',
  pairDebugSession: 'Debug session',
  pairFixReady_one: '{{count}} fix ready',
  pairFixReady_other: '{{count}} fixes ready',
  pairCiteHandleAria: 'Cite {{name}} in the Pair chat (click, or drag onto the chat)',
  pairCiteHandleTitle: 'Click or drag onto the Pair chat to cite this step',
  pairEdgeInsertAria: 'Insert a step between {{from}} and {{to}}',
  pairEdgeInsertTitle: 'Insert a step here',
  pairEdgeInsertFraming: 'Insert a step between “{{from}}” and “{{to}}”:',
  pairEdgeMoreFit_one: '{{count}} more step fits — search the palette.',
  pairEdgeMoreFit_other: '{{count}} more steps fit — search the palette.',
  pairGroupLabelName: 'Group',
  pairGroupLabelContent_one: '{{count}} step',
  pairGroupLabelContent_other: '{{count}} steps',
  pairSelectionFraming: 'About these steps: {{names}}.',
  pairSelectionToolbarAria_one: 'Pair actions for {{count}} step',
  pairSelectionToolbarAria_other: 'Pair actions for {{count}} steps',
  pairLabelGroup: 'Label as group',
  pairExtract: 'Extract as sub-chain',
  pairExtractPrompt: 'Suggest extracting these steps into a sub-workflow (a core.subWorkflow node), as a suggestion I can review: {{names}}.',
  pairExplainManyPrompt: 'Explain what these steps do together and how data flows between them: {{names}}.',
  pairPeerGhost: 'Suggested by {{name}}',
  pairPeerGhostAnon: 'Shared suggestion',
  pairGhostRestoreAria: 'Restore dropped change {{n}}: {{name}}',
  pairSuggestedHere: 'Fits after {{name}}',
  pairSuggestedMore_one: '{{count}} more fits — search the palette.',
  pairSuggestedMore_other: '{{count}} more fit — search the palette.',
  pairStop: 'Stop',
  pairFirstDraftChains: 'Or start from a template:',
  pairChainFailed: 'Couldn’t draw “{{name}}”. Open it from the gallery instead.',
  // ADR 0773 P11
  shortcutPairCloseReview: 'Hide the suggestion review (changes stay drawn)',
  pairAnnReviewClosed: 'Review hidden. The changes are still drawn; press J to review again.',
  pairFitToSuggestion: 'Fit to suggestion',
  pairFreshCurrent: 'Applies to the sheet as it is now.',
  pairFreshStale_one: '{{count}} change is out of date: the sheet moved since it was drawn.',
  pairFreshStale_other: '{{count}} changes are out of date: the sheet moved since they were drawn.',
  pairStatusSteps_one: '{{count}} step',
  pairStatusSteps_other: '{{count}} steps',
  pairStatusConnections_one: '{{count}} connection',
  pairStatusConnections_other: '{{count}} connections',
  pairStatusSelected: 'selected: {{name}}',
  pairStatusSelectedMany: '{{count}} selected',
  pairStatusHide: 'to hide',
  pairCiteRunStep: 'Ask the Architect about “{{name}}” in this run',
  pairTemplateSteps_one: '{{count}} step',
  pairTemplateSteps_other: '{{count}} steps',
  pairTemplateGates_one: '{{count}} approval',
  pairTemplateGates_other: '{{count}} approvals',
  pairTemplateBrowseAll: 'Browse all templates',
  pairCmdkAsk: 'Ask the Architect: “{{query}}”',
  pairCmdkAskHint: 'Opens the Pair tab and sends this',
  pairCmdkGroup: 'Workflow Architect',
  pairDecisionNotSaved: 'Your keep/drop choice wasn’t saved, so it may reappear after a reload. The canvas change itself is saved.',
} as const;
